import { useDispatch } from "react-redux";
import styled from "styled-components";
import type { AppDispatch } from "types/redux-dispatch";

import { closeModal } from "libs/modals";
import { Alert } from "ui";
import { Size, Type } from "ui/alert/alert";
import { Link } from "ui/Link";

import { ConfirmWithText } from "ui/modals/ConfirmWithText";

import {
  REMOVE_USER_ACCOUNT,
  removeAccountFx,
} from "../../view/remove-account.model";
import { fetchAccountGroups } from "features/account-groups-form/accountsGroups.model";
import { refreshAccountsList } from "features/account-groups-form/utils";
import { AccountType } from "shared/types/accounts";
import { openSupportChat } from "libs/support/open-support-chat";

interface DeleteUserTypes {
  account: AccountType;
  accountName: string;
}

const ConfirmText = "remove account";

export const RemoveUserAccountModal = (props: DeleteUserTypes) => {
  const { accountName, account } = props;
  const dispatch = useDispatch<AppDispatch>();

  const closeDeleteModal = () => {
    dispatch(closeModal({ type: REMOVE_USER_ACCOUNT }));
  };

  const submit = async () => {
    const result = await dispatch(removeAccountFx(account) as never);

    if (removeAccountFx.rejected.match(result as never)) {
      return;
    }

    // Refresh accounts list to sync Redux store with Effector store
    await refreshAccountsList();

    if (account.loginAlias) {
      await dispatch(fetchAccountGroups());
    }

    closeDeleteModal();
  };

  const openChat = () => {
    openSupportChat("", "accounts");
  };

  return (
    <ConfirmWithText
      closeModal={closeDeleteModal}
      title={`Remove ${accountName}`}
      subTitle={`Are you sure you want to remove ${accountName} from Sked?`}
      defaultConfirmText={ConfirmText}
      confirmButtonText="Remove Account"
      onSubmit={submit}
    >
      <>
        <AlertStyled type={Type.warning} size={Size.small}>
          Once removed, any upcoming posts for this account will not be posted.
          Within Sked, scheduled posts or drafts will also be deleted and not
          recoverable. If you&apos;re experiencing any issues with this account,
          please{" "}
          <LinkStyled color="warning" onClick={openChat} underline="never">
            contact support
          </LinkStyled>
          .
        </AlertStyled>
        <Details>
          To confirm, enter <i>&lsquo;remove account&rsquo;</i> in the field
          below.
        </Details>
      </>
    </ConfirmWithText>
  );
};

const Text = styled.p`
  font-size: 14px;
  line-height: 17px;
  font-weight: 300;
`;

const Details = styled(Text)`
  margin-top: 26px;
`;

const AlertStyled = styled(Alert)`
  > div {
    font-weight: 300 !important;
  }
`;
const LinkStyled = styled(Link)`
  font-weight: 500;
  text-decoration: none !important;
`;
